import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  createFilterOptions,
} from "@mui/material";
import AutoCompleteTagInput from "../../components/AutoCompleteTagInput/AutoCompleteTagInput";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import Loader from "../../components/Loader/Loader";

const AssociateBootcamp = ({
  open,
  onClose,
  handleAssociateBootcampSubmit,
  getBootcampData,
  associateBootcampDetails,
  handleAssociateBootcampDetailsChange,
  associateBootcampApiError,
  loader,
  module,
}) => {
  const getBootcampinfo = useSelector((state) => {
    return state?.bootcamp;
  });

  const moduleOptionLoading = getBootcampinfo?.moduleOptionLoading;
  const getBootcampOptionsLoader = getBootcampinfo?.getBootcampOptionsLoader;

  const initialErrorState = {
    bootcampIds: "",
  };
  const [errors, setErrors] = useState(initialErrorState);
  const [defaultBootcampIds, setDefaultBootcampIds] = useState([]);

  useEffect(() => {
    if (open && associateBootcampDetails?.bootcampIds) {
      // Store default bootcamp IDs (non-removable) when modal opens
      // Only capture once when modal opens, not on every change
      const defaultIds =
        associateBootcampDetails?.bootcampIds?.map(
          (bootcamp) => bootcamp?.id
        ) || [];
      setDefaultBootcampIds(defaultIds);
    } else if (!open) {
      setDefaultBootcampIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const validateFields = () => {
    const { bootcampIds } = associateBootcampDetails;
    let newErrors = {
      bootcampIds:
        bootcampIds && bootcampIds.length > 0 ? "" : "Bootcamps are required",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every(
      (error) => error === "" || !error?.length
    );
  };

  const handleSubmit = () => {
    if (validateFields()) {
      handleAssociateBootcampSubmit();
    }
  };

  const handleOnClose = () => {
    setErrors(initialErrorState);
    setDefaultBootcampIds([]);
    onClose();
  };

  // Handler function to delete the chip
  const handleChipDelete = (optionToDelete) => {
    // Prevent deletion of default bootcamps
    if (defaultBootcampIds?.includes(optionToDelete?.id)) {
      return;
    }
    const newBootcampIds = associateBootcampDetails?.bootcampIds?.filter(
      (option) => option?.id !== optionToDelete?.id
    );
    handleAssociateBootcampDetailsChange("bootcampIds", newBootcampIds);
    setErrors((prev) => {
      return {
        ...prev,
        bootcampIds: "",
      };
    });
  };

  return (
    <>
      {(moduleOptionLoading || getBootcampOptionsLoader || loader) && (
        <Loader />
      )}
      <Dialog
        className="modal-drawer-container"
        open={open}
        onClose={handleOnClose}
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "1rem",
            boxShadow: "0rem 0.6rem 3rem 0rem #OD",
            zIndex: 1300,
            maxWidth: "29.75rem",
          },
        }}
      >
        <DialogTitle className="dialog-title">Associate Bootcamp</DialogTitle>
        <DialogContent
          className="dialog-content"
          sx={{ marginBottom: "0 !important" }}
        >
          <Box sx={{ marginBottom: "0.75rem" }}>
            <AutoCompleteTagInput
              label="Bootcamps"
              placeholder={
                associateBootcampDetails?.bootcampIds?.length
                  ? null
                  : "Select Bootcamp"
              }
              getOptionLabel={(option) => option.name}
              options={getBootcampData}
              selectedValue={associateBootcampDetails?.bootcampIds}
              filterOptions={(options, params) => {
                const filter = createFilterOptions();
                const filtered = filter(options, params);
                if (getBootcampData.length === 0) {
                  return [{ name: "No options", all: false }];
                }
                return [
                  { name: "Select All", startDate: dayjs(), all: true },
                  ...filtered,
                ];
              }}
              onChange={(event, newValue) => {
                const idCounts = newValue.reduce((counts, { id }) => {
                  counts[id] = (counts[id] || 0) + 1;
                  return counts;
                }, {});

                const uniqueNewValue = newValue.filter(({ id }) => {
                  return idCounts[id] === 1;
                });

                if (newValue.find((option) => option.all)) {
                  // When selecting all, ensure default bootcamps are always included
                  const allBootcamps =
                    associateBootcampDetails?.bootcampIds?.length ===
                    getBootcampData?.length
                      ? []
                      : getBootcampData;
                  // Always include default bootcamps
                  const defaultBootcamps =
                    getBootcampData?.filter((bootcamp) =>
                      defaultBootcampIds?.includes(bootcamp?.id)
                    ) || [];
                  const newBootcamps =
                    allBootcamps?.filter(
                      (bootcamp) => !defaultBootcampIds?.includes(bootcamp?.id)
                    ) || [];
                  return handleAssociateBootcampDetailsChange("bootcampIds", [
                    ...defaultBootcamps,
                    ...newBootcamps,
                  ]);
                }

                // Ensure default bootcamps are always included
                // Get default bootcamps from current selection to handle clear button case
                const defaultBootcamps =
                  associateBootcampDetails?.bootcampIds?.filter((bootcamp) =>
                    defaultBootcampIds?.includes(bootcamp?.id)
                  ) || [];
                const newSelectedBootcamps =
                  uniqueNewValue?.filter(
                    (bootcamp) => !defaultBootcampIds?.includes(bootcamp?.id)
                  ) || [];
                handleAssociateBootcampDetailsChange("bootcampIds", [
                  ...defaultBootcamps,
                  ...newSelectedBootcamps,
                ]);
                setErrors((prev) => {
                  return {
                    ...prev,
                    bootcampIds: "",
                  };
                });
              }}
              required={true}
              error={!!errors.bootcampIds || associateBootcampApiError}
              helperText={errors.bootcampIds}
              renderOption={(props, option, { selected }) => {
                if (option.name === "No options") {
                  return (
                    <Box className="auto-complete-no-options">
                      <Box>{option?.name}</Box>
                    </Box>
                  );
                }
                const optionChecked =
                  (option.all &&
                    associateBootcampDetails?.bootcampIds?.length ===
                      getBootcampData?.length) ||
                  associateBootcampDetails?.bootcampIds?.find(
                    (e) => e?.id === option?.id
                  ) != null;
                const isDefaultBootcamp = defaultBootcampIds?.includes(
                  option?.id
                );
                return (
                  <li
                    {...props}
                    key={props?.id}
                    className={
                      optionChecked
                        ? "multi-tag-edit-list-selected"
                        : "multi-tag-style"
                    }
                  >
                    <Box className="multi-tag-input-list-padding">
                      <Checkbox
                        className="auto-complete-checkbox-list"
                        size="small"
                        checked={optionChecked}
                        color="secondary"
                        disabled={isDefaultBootcamp}
                      />
                      <Box className="flex-options">
                        <Box>{option?.name}</Box>
                        {option?.name === "Select All" ? null : (
                          <Box>
                            {option?.startDate
                              ? dayjs(option?.startDate)?.format("DD/MM/YYYY")
                              : null}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const isDefaultBootcamp = defaultBootcampIds?.includes(
                    option?.id
                  );
                  return (
                    <Chip
                      {...getTagProps(index)}
                      key={index}
                      label={option?.name}
                      onDelete={
                        isDefaultBootcamp
                          ? undefined
                          : () => handleChipDelete(option)
                      }
                    />
                  );
                })
              }
              getOptionDisabled={(option) =>
                defaultBootcampIds?.includes(option?.id)
              }
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ padding: "0rem 2rem 1.875rem" }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleOnClose}
            className="block-button"
          >
            <Typography variant="outlineBtnLabel">Cancel</Typography>
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="secondary"
            className="block-button"
          >
            <Typography variant="outlineBtnLabel">Associate</Typography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssociateBootcamp;
